import React, {
  ReactElement, useEffect, useRef, useState,
} from 'react';
import DOMPurify from 'dompurify';
import {
  Col, FormGroup, Heading, Input, InsetText, Label, LoaderButton, Row, Select, Textarea,
} from 'idsk-react-components';
import {
  SafeParameterSetter,
// eslint-disable-next-line max-len
} from 'slovlex-idsk-react-components/es/slovlex-idsk-react-components/components/PredpisSearchModal/types/SafeParameterSetter';
import Reaptcha from 'reaptcha';
import jwt from 'jwt-decode';
import NahlasenieChybyItems from '../../model/NahlasenieChybyItems';
import './nahlasenieChyby.scss';
import { NahlasenieChybyEnum } from '../../constants/NahlasenieChybyEnum';
import NahlasenieChybyRequest from '../../model/NahlasenieChybyRequest';
import { postNahlasenieChybyNotification }
  from '../../client/external/evidencnaaplikacia/nahlasenieChybyNotificationClient';
import { useClient } from '../../context/ClientStatusContext';
import { useSuccessMessageContext } from '../../context/SuccessMessageContext';
import useFieldErrorMessages from '../useFieldErrorMessages';
import { useSecurityContext } from '../../context/SecurityContext';

interface Props {
  nahlasenieChybyItems: NahlasenieChybyItems | null | undefined
}

const NahlasenieChyby = ({ nahlasenieChybyItems }: Props): ReactElement => {
  const [nahlasenieChybyRequest, setNahlasenieChybyRequest] = useState<NahlasenieChybyRequest>(
    {
      oblast: NahlasenieChybyEnum.EZBIERKA,
      popisChyby: '',
      email: '',
    } as NahlasenieChybyRequest,
  );
  const [emailLoadedFromAccessToken, setEmailLoadedFromAccessToken] = useState(false);

  const { accessToken } = useSecurityContext();

  const decodeEmailFromAccessToken = (): string => {
    if (accessToken && accessToken !== 'undefined' && accessToken !== 'null') {
      const user = jwt(accessToken);
      // @ts-ignore
      if (user?.email) {
        // @ts-ignore
        return user.email;
      }
    }
    return '';
  };

  const updateField: SafeParameterSetter<NahlasenieChybyRequest> = (fieldName, value) => {
    setNahlasenieChybyRequest(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const {
    wrapClient: nahlasenieChybyNotificationClient,
    status: nahlasenieChybyNotificationStatus,
  } = useClient(postNahlasenieChybyNotification);
  const { displaySuccess } = useSuccessMessageContext();
  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();

  useEffect(() => {
    if (accessToken && !emailLoadedFromAccessToken) {
      updateField('email', decodeEmailFromAccessToken());
      setEmailLoadedFromAccessToken(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, emailLoadedFromAccessToken]);

  const [verified, setVerified] = useState(false);
  const captchaRef = useRef(null);

  const onSubmit = async (e): Promise<void> => {
    e.preventDefault();
    await nahlasenieChybyNotificationClient(nahlasenieChybyRequest);
    displaySuccess('Váš formulár bol úspešne odoslaný.');
    clearForm();
    window.parent.postMessage({
      type: 'scroll-to',
      top: 0,
    }, '*');
  };

  const clearForm = (): void => {
    setNahlasenieChybyRequest({
      oblast: NahlasenieChybyEnum.EZBIERKA,
      popisChyby: '',
      email: decodeEmailFromAccessToken(),
    } as NahlasenieChybyRequest);
    // @ts-ignore
    captchaRef.current?.reset();
    setVerified(false);
  };

  const onVerify = (): void => {
    setVerified(true);
  };
  const onExpire = (): void => {
    setVerified(false);
  };

  return (
    <div className="nahlasenie-chyby">
      <Heading
        className="page-heading"
        size="xl"
      >
        Nahlásenie chyby
      </Heading>
      <div className="main">
        <Row className="sub-heading">
          <Col width="two-thirds">
            <span
              className="popis"
              /* eslint-disable-next-line react/no-danger */
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(nahlasenieChybyItems?.popis || '') }}
            />
            {nahlasenieChybyItems?.upozornenie && (
              <InsetText type="warn-info">
                <strong>
                  {/* eslint-disable-next-line react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(nahlasenieChybyItems?.upozornenie) }} />
                </strong>
              </InsetText>
            )}
          </Col>
        </Row>
        <Row>
          <Col width="one-half">
            <FormGroup>
              <Label htmlFor="typNahlasenieChyby" className="nahlasenie-chyby__label">
                Oblasť
              </Label>
              <Select
                id="typNahlasenieChyby"
                width="full"
                value={nahlasenieChybyRequest.oblast}
                error={getFieldErrorMessage && getFieldErrorMessage('oblast')}
                onChange={({ target }) => {
                  updateField('oblast', target.value);
                  clearFieldErrorMessage('oblast');
                }}
              >
                {Object.values(NahlasenieChybyEnum)
                  .map(typ => (
                    <option
                      key={typ}
                      value={typ}
                    >
                      {typ}
                    </option>
                  ))}
              </Select>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col width="one-half">
            <FormGroup>
              <Label htmlFor="nahlasenie-chyby-text">Popis chyby</Label>
              <Textarea
                id="nahlasenie-chyby-text"
                rows={3}
                type="text"
                maxLength={1000}
                name="popisChyby"
                value={nahlasenieChybyRequest?.popisChyby}
                error={getFieldErrorMessage && getFieldErrorMessage('popisChyby')}
                showRemaining
                expandHeight
                onChange={({ target }) => {
                  updateField('popisChyby', target.value);
                  clearFieldErrorMessage('popisChyby');
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col width="one-half">
            <FormGroup>
              <Label htmlFor="nahlasenie-chyby-email">Kontaktný e-mail</Label>
              <Input
                id="nahlasenie-chyby-email"
                type="text"
                value={nahlasenieChybyRequest?.email}
                error={getFieldErrorMessage && getFieldErrorMessage('email')}
                onChange={({ target }) => {
                  updateField('email', target.value);
                  clearFieldErrorMessage('email');
                }}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <div className="g-recaptcha" data-sitekey={process.env.REACT_APP_SITE_KEY} />
          <Col width="one-half">
            <FormGroup>
              <Reaptcha
                sitekey={process.env.REACT_APP_SITE_KEY}
                ref={captchaRef}
                onVerify={onVerify}
                onExpire={onExpire}
                hl="sk"
              />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col width="one-half">
            <LoaderButton
              type="submit"
              size="large"
              onClick={onSubmit}
              disabled={!verified}
              loading={nahlasenieChybyNotificationStatus.loading}
            >Odoslať
            </LoaderButton>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default NahlasenieChyby;
